import React, { useEffect, useState } from "react";
import RequestServices from "../../../services/RequestServies";
import Swal from "sweetalert2";

// All HO-returned items in a selected Storekeeper store.
const ReversedByHoStorekeeper = () => {
    const user = JSON.parse(localStorage.getItem("userDetails") || "null");
    const stores = user?.data?.user?.store || [];
    const [selectedStore, setSelectedStore] = useState(stores[0]?._id || "");
    const [requisitions, setRequisitions] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [merging, setMerging] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const [keepRequisitionId, setKeepRequisitionId] = useState("");
    const [sourceRequisitionIds, setSourceRequisitionIds] = useState([]);
    const [showMergePreview, setShowMergePreview] = useState(false);

    const toggleSourceRequisition = (id) => {
        setSourceRequisitionIds((current) =>
            current.includes(id)
                ? current.filter((value) => value !== id)
                : [...current, id]
        );
    };

    const mergePreview = (() => {
        const keepItems = items.filter(
            (item) => String(item.requisitionNo?._id) === keepRequisitionId
        );

        const sourceItems = items.filter((item) =>
            sourceRequisitionIds.includes(String(item.requisitionNo?._id))
        );

        const rows = new Map();

        keepItems.forEach((item) => {
            const inventoryId = String(item.inventory?._id || "");
            if (!inventoryId) return;

            const existing = rows.get(inventoryId);
            if (existing) {
                existing.currentQty += Number(item.qtyRequired) || 0;
            } else {
                rows.set(inventoryId, {
                    inventoryId,
                    partNo: item.inventory?.masterItem?.partNo ?? "-",
                    description: item.inventory?.masterItem?.description || "-",
                    currentQty: Number(item.qtyRequired) || 0,
                    addedQty: 0,
                    alreadyExists: true,
                });
            }
        });

        sourceItems.forEach((item) => {
            const inventoryId = String(item.inventory?._id || "");
            if (!inventoryId) return;

            const existing = rows.get(inventoryId);
            if (existing) {
                existing.addedQty += Number(item.qtyRequired) || 0;
            } else {
                rows.set(inventoryId, {
                    inventoryId,
                    partNo: item.inventory?.masterItem?.partNo ?? "-",
                    description: item.inventory?.masterItem?.description || "-",
                    currentQty: 0,
                    addedQty: Number(item.qtyRequired) || 0,
                    alreadyExists: false,
                });
            }
        });

        return [...rows.values()];
    })();

    useEffect(() => {
        if (!selectedStore) {
            setRequisitions([]);
            setItems([]);
            setKeepRequisitionId("");
            setSourceRequisitionIds([]);
            setShowMergePreview(false);
            return;
        }

        let active = true;
        setLoading(true);
        setError("");
        setRequisitions([]);
        setKeepRequisitionId("");
        setSourceRequisitionIds([]);
        setShowMergePreview(false);
        setItems([]);

        RequestServices.getHoReturnedItemsByStore(selectedStore)
            .then((response) => {
                if (!active) return;
                setRequisitions(response?.data?.requisitions || []);
                setItems(response?.data?.items || []);
            })
            .catch((err) => {
                if (active) {
                    setError(err?.response?.data?.message || "Could not load HO-returned items.");
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => { active = false; };
    }, [selectedStore, refreshKey]);

  const handleConfirmMerge = async () => {
    if (
        !selectedStore ||
        !keepRequisitionId ||
        sourceRequisitionIds.length === 0 ||
        merging
    ) {
        return;
    }

    const keepNumber = requisitions.find(
        (request) => String(request._id) === keepRequisitionId
    )?.requisitionNo;

    const confirmation = await Swal.fire({
        title: "Merge requisitions?",
        html: `
            <div style="text-align: left;">
                <p>Merge <strong>${sourceRequisitionIds.length}</strong>
                requisition(s) into <strong>${keepNumber}</strong>?</p>
                <p>The selected requisition will be sent to the
                <strong>Project Manager for approval</strong>.</p>
                <p>The other requisitions will be marked as merged.</p>
            </div>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Merge & Submit",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#36b9ad",
        reverseButtons: true,
    });

    if (!confirmation.isConfirmed) return;

    try {
        setMerging(true);
        setError("");

        const response = await RequestServices.mergeHoReturnedRequisitions({
            storeId: selectedStore,
            keepRequisitionId,
            sourceRequisitionIds,
        });

        setRefreshKey((current) => current + 1);

        await Swal.fire({
            title: "Submitted successfully",
            text:
                response?.data?.message ||
                `Requisition ${keepNumber} was sent to the Project Manager for approval.`,
            icon: "success",
            confirmButtonColor: "#36b9ad",
        });
    } catch (err) {
        await Swal.fire({
            title: "Merge failed",
            text:
                err?.response?.data?.message ||
                "Could not merge requisitions. Please try again.",
            icon: "error",
            confirmButtonColor: "#d33",
        });
    } finally {
        setMerging(false);
    }
};

    return (
        <div className="cm-content-box box-primary">
            <div className="content-title">
                <div className="cpa">
                    <i className="fa fa-undo me-2" aria-hidden="true" />
                    Returned by HO
                    <span className="badge bg-warning text-dark ms-2">
                        {requisitions.length} requisitions / {items.length} items
                    </span>
                </div>
            </div>

            <div className="card-body">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                <div className="border rounded-3 p-3 mb-3">
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-md-6 col-xl-3">
                            <label htmlFor="ho-returned-store" className="form-label">
                                Store
                            </label>
                            <select
                                id="ho-returned-store"
                                className="form-select w-100"
                                style={{ minHeight: 48, borderRadius: 12 }}
                                value={selectedStore}
                                onChange={(event) => setSelectedStore(event.target.value)}
                            >
                                {stores.length === 0 && (
                                    <option value="">No store assigned</option>
                                )}
                                {stores.map((store) => (
                                    <option key={store._id} value={store._id}>
                                        {store.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                            <label htmlFor="keep-requisition" className="form-label">
                                Keep requisition
                            </label>
                            <select
                                id="keep-requisition"
                                className="form-select w-100"
                                style={{ minHeight: 48, borderRadius: 12 }}
                                value={keepRequisitionId}
                                disabled={loading || requisitions.length < 2}
                                onChange={(event) => {
                                    const id = event.target.value;
                                    setKeepRequisitionId(id);
                                    setSourceRequisitionIds((current) =>
                                        current.filter((value) => value !== id)
                                    );
                                    setShowMergePreview(false);
                                }}
                            >
                                <option value="">Select requisition</option>
                                {requisitions.map((request) => (
                                    <option key={request._id} value={request._id}>
                                        {request.requisitionNo}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-12 col-md-8 col-xl-4">
                            <span id="merge-source-label" className="form-label d-block">
                                Merge from
                            </span>
                            <div
                                role="group"
                                aria-labelledby="merge-source-label"
                                className="border rounded-3 px-3 py-2"
                                style={{
                                    minHeight: 48,
                                    maxHeight: 120,
                                    overflowY: "auto",
                                }}
                            >
                                {!keepRequisitionId ? (
                                    <span className="text-muted">
                                        Select a requisition to keep first
                                    </span>
                                ) : (
                                    requisitions
                                        .filter(
                                            (request) =>
                                                String(request._id) !== keepRequisitionId
                                        )
                                        .map((request) => (
                                            <label
                                                key={request._id}
                                                className="d-inline-flex align-items-center gap-2 me-3 mb-1"
                                                style={{ cursor: "pointer" }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0"
                                                    checked={sourceRequisitionIds.includes(
                                                        String(request._id)
                                                    )}
                                                    onChange={() => {
                                                        toggleSourceRequisition(
                                                            String(request._id)
                                                        );
                                                        setShowMergePreview(false);
                                                    }}
                                                />
                                                <span>{request.requisitionNo}</span>
                                            </label>
                                        ))
                                )}
                            </div>
                        </div>

                        <div className="col-12 col-md-4 col-xl-2">
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                style={{ minHeight: 48, borderRadius: 12 }}
                                disabled={
                                    loading ||
                                    !keepRequisitionId ||
                                    sourceRequisitionIds.length === 0
                                }
                                onClick={() => setShowMergePreview(true)}
                            >
                                Preview merge
                            </button>
                        </div>
                    </div>
                </div>

                {showMergePreview && (
                    <div className="border rounded-3 p-3 p-md-4 mb-4">
                        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                            <div>
                                <h5 className="mb-1">Review requisition merge</h5>
                                <div className="text-muted small">
                                    {sourceRequisitionIds.length} requisition(s) will merge into{" "}
                                    <strong>
                                        {
                                            requisitions.find(
                                                (request) =>
                                                    String(request._id) === keepRequisitionId
                                            )?.requisitionNo
                                        }
                                    </strong>
                                </div>
                            </div>

                            <span className="badge bg-light text-dark border">
                                {mergePreview.length} item(s) after merge
                            </span>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Part No</th>
                                        <th>Description</th>
                                        <th className="text-end">Existing Qty</th>
                                        <th className="text-end">Qty to Add</th>
                                        <th className="text-end">Final Qty</th>
                                        <th>Change</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mergePreview.map((row) => (
                                        <tr key={row.inventoryId}>
                                            <td>{row.partNo}</td>
                                            <td>{row.description}</td>
                                            <td className="text-end">{row.currentQty}</td>
                                            <td className="text-end">+{row.addedQty}</td>
                                            <td className="text-end">
                                                <strong>
                                                    {row.currentQty + row.addedQty}
                                                </strong>
                                            </td>
                                            <td>
                                                <span
                                                    className={
                                                        row.alreadyExists
                                                            ? "badge bg-info text-dark"
                                                            : "badge bg-success"
                                                    }
                                                >
                                                    {row.alreadyExists
                                                        ? "Increase quantity"
                                                        : "Add new item"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mt-3">
                            <span className="text-muted small">
                                The selected requisition will be sent to the Project Manager
                                for approval.
                            </span>

                            <button
                                type="button"
                                className="btn btn-success px-4"
                                disabled={merging || mergePreview.length === 0}
                                onClick={handleConfirmMerge}
                            >
                                {merging
                                    ? "Submitting..."
                                    : "Merge & Submit for Approval"}
                            </button>
                        </div>
                    </div>
                )}

                <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle mb-0">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Requisition No</th>
                                <th>Part No</th>
                                <th>Description</th>
                                <th>Requested Qty</th>
                                <th>HO Remark</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        No items returned by HO.
                                    </td>
                                </tr>
                            ) : (
                                items.map((item, index) => (
                                    <tr key={item._id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            {item?.requisitionNo?.requisitionNo ?? "-"}
                                        </td>
                                        <td>
                                            {item?.inventory?.masterItem?.partNo ?? "-"}
                                        </td>
                                        <td>
                                            {item?.inventory?.masterItem?.description ||
                                                "-"}
                                        </td>
                                        <td>{item.qtyRequired ?? "-"}</td>
                                        <td
                                            style={{
                                                whiteSpace: "normal",
                                                minWidth: 220,
                                            }}
                                        >
                                            {item.reverseByHoRemark || "-"}
                                        </td>
                                        <td>
                                            <span className="badge bg-warning text-dark">
                                                Returned by HO
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ReversedByHoStorekeeper;
